import { Box, TextField, TextFieldProps, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { FIELD_SECURITY_CONFIGS } from '../../../../../../corelogic/domain/constants/fieldValidation.constants';
import {
  validateInputRealtime,
  ValidationResult,
} from '../../../../../../corelogic/domain/utils/realtimeSecurityValidation.utils';

interface SecureTextFieldProps extends Omit<TextFieldProps, 'onChange'> {
  value: string;
  onChange: (value: string, validation: ValidationResult) => void;
  fieldType?:
    | 'text'
    | 'email'
    | 'username'
    | 'name'
    | 'description'
    | 'tournament_password';
  fieldName?: string;
  showWarnings?: boolean;
  enableRealTimeValidation?: boolean;
  showCharacterCounter?: boolean;
  characterCountWarningThreshold?: number; // Percentage (0.8 = 80%)
}

/**
 * 🔒 SECURE TEXT FIELD COMPONENT
 *
 * Implements layers 1 and 2 of our security approach:
 * - Real-time validation with warnings (Layer 1)
 * - Pre-submit analysis feedback (Layer 2)
 *
 * Features:
 * - Shows security warnings directly below input
 * - Provides immediate feedback on security issues
 * - Visual feedback with color-coded warnings
 * - Debounced validation to avoid performance issues
 * - Optional character counter with color-coded thresholds
 */
export const SecureTextField: React.FC<SecureTextFieldProps> = ({
  value,
  onChange,
  fieldType = 'text',
  fieldName = 'input',
  showWarnings = true,
  enableRealTimeValidation = true,
  showCharacterCounter = false,
  characterCountWarningThreshold = 0.8,
  helperText,
  error,
  inputProps,
  ...textFieldProps
}) => {
  const [validation, setValidation] = useState<ValidationResult>({
    isValid: true,
    warnings: [],
    sanitizedValue: value,
    originalValue: value,
  });
  // Add focus state
  const [isFocused, setIsFocused] = useState(false);

  // Get max length from security config or inputProps
  const configKey =
    fieldType.toUpperCase() as keyof typeof FIELD_SECURITY_CONFIGS;
  const securityConfig =
    FIELD_SECURITY_CONFIGS[configKey] || FIELD_SECURITY_CONFIGS.TEXT;
  const maxLength = inputProps?.maxLength || securityConfig.maxLength;

  // Simple debounced validation
  const debouncedValidator = useCallback(
    (callback: (result: ValidationResult) => void, delay: number = 300) => {
      let timeoutId: NodeJS.Timeout;

      return (value: string, fieldType: string, fieldName: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          const result = validateInputRealtime(
            value,
            fieldType as any,
            fieldName,
          );
          callback(result);
        }, delay);
      };
    },
    [],
  );

  // Create debounced validator instance
  const performDebouncedValidation = useCallback(
    debouncedValidator((result: ValidationResult) => {
      setValidation(result);
    }, 300),
    [debouncedValidator],
  );

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    // Always call parent onChange
    const currentValidation = validateInputRealtime(
      newValue,
      fieldType,
      fieldName,
    );
    onChange(newValue, currentValidation);

    // If real-time validation is enabled, run debounced validation
    if (enableRealTimeValidation) {
      performDebouncedValidation(newValue, fieldType, fieldName);
    }
  };

  // Update validation when value changes externally
  useEffect(() => {
    if (enableRealTimeValidation) {
      const result = validateInputRealtime(value, fieldType, fieldName);
      setValidation(result);
    }
  }, [value, fieldType, fieldName, enableRealTimeValidation]);

  // Determine field colors based on validation
  const hasHighSeverityWarnings = validation.warnings.some(
    (w) => w.severity === 'high',
  );

  const fieldColor = hasHighSeverityWarnings ? '#f44336' : undefined;

  // Get warning message color
  const getWarningColor = (severity: 'low' | 'high'): string => {
    switch (severity) {
      case 'high':
        return '#f44336';
      case 'low':
        return '#2196f3';
      default:
        return '#666666';
    }
  };

  // Character counter styling
  const getCharacterCountStyle = () => {
    if (!showCharacterCounter || !maxLength) return {};

    const warningThreshold = maxLength * characterCountWarningThreshold;

    if (value.length >= maxLength) {
      return { color: '#ff0000' };
    } else if (value.length >= warningThreshold) {
      return { color: '#ff9900' };
    } else {
      return { color: '#999999' };
    }
  };

  // Check if we have content to show below the input
  const hasCharacterCounter = showCharacterCounter && maxLength;
  // Only show low-risk warnings when not focused (on blur)
  const filteredWarnings = isFocused
    ? validation.warnings.filter((w) => w.severity === 'high')
    : validation.warnings;
  const hasWarnings = showWarnings && filteredWarnings.length > 0;
  const shouldShowFooter = hasCharacterCounter || hasWarnings;

  return (
    <Box>
      <TextField
        {...textFieldProps}
        value={value}
        onChange={handleInputChange}
        error={error || hasHighSeverityWarnings}
        helperText={helperText}
        inputProps={{
          maxLength,
          ...inputProps,
        }}
        InputProps={{
          ...textFieldProps.InputProps,
          sx: {
            ...textFieldProps.InputProps?.sx,
            ...(fieldColor && {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: fieldColor,
              },
            }),
          },
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />

      {/* Combined Character Counter and Error Messages - Closer to input */}
      {shouldShowFooter && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mt: 0.05,
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {/* Error Messages - Left side, can wrap */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            {hasWarnings &&
              filteredWarnings.map((warning, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{
                    color: getWarningColor(warning.severity),
                    fontSize: '0.75rem',
                    mb: index < validation.warnings.length - 1 ? 0.1 : 0,
                    lineHeight: 1.2,
                    wordBreak: 'break-word', // Allow breaking long words
                  }}
                >
                  {warning.message}
                </Typography>
              ))}
          </Box>

          {/* Character Counter - Right side, always on same line */}
          {hasCharacterCounter && (
            <Box
              sx={{
                fontSize: '0.75rem',
                flexShrink: 0, // Don't shrink the counter
                alignSelf: 'flex-start', // Align to top when messages wrap
                ...getCharacterCountStyle(),
              }}
            >
              {value.length}/{maxLength}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
